import { useRef, useState, useEffect } from 'react';
import { TableAction } from '@/types';
import { useClickOutside } from '@/hooks';
import Button from '@/components/ui/Button.tsx';
import { ChevronDown, Menu } from 'lucide-react';

interface DropdownActionsProps<T> {
  actions: TableAction<T>[];
  item: T;
  mobileIconOnly?: boolean;
  primaryAction?: TableAction<T> & { disabled?: boolean };
  triggerLabel?: string;
}

const DropdownActions = <T,>({ actions, item, mobileIconOnly = false, primaryAction, triggerLabel = 'Opciones' }: DropdownActionsProps<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useClickOutside(dropdownRef, () => setIsOpen(false));

  const visibleActions = actions.filter(action => !action.show || action.show(item));
  const visibleCount = visibleActions.length;

  useEffect(() => {
    if (!isOpen) return;

    const updateMenuPosition = () => {
      if (!dropdownRef.current) return;

      const rect = dropdownRef.current.getBoundingClientRect();
      const menuHeight = menuRef.current?.offsetHeight || visibleCount * 40;
      const menuWidth = menuRef.current?.offsetWidth || 192;
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const shouldOpenUp = spaceBelow < menuHeight && spaceAbove > menuHeight;
      const offset = 8;

      const top = shouldOpenUp
        ? rect.top - menuHeight - offset
        : rect.bottom + offset;

      let left = rect.right - menuWidth;
      left = Math.min(left, viewportWidth - menuWidth - offset);
      left = Math.max(offset, left);

      setMenuPosition({
        top: Math.max(offset, top),
        left,
      });
    };

    updateMenuPosition();

    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);

    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [isOpen, visibleCount]);

  return (
    <div
      className={primaryAction ? 'relative inline-flex shrink-0 whitespace-nowrap' : 'relative'}
      ref={dropdownRef}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && isOpen) {
          event.stopPropagation();
          setIsOpen(false);
          triggerRef.current?.focus();
        }
      }}
    >
      {primaryAction && (
        <Button
          type="button"
          variant="outline"
          size="xs"
          onClick={() => {
            setIsOpen(false);
            primaryAction.onClick(item);
          }}
          disabled={primaryAction.disabled}
          title={primaryAction.label}
          aria-label={primaryAction.label}
          className={`rounded-r-none focus:ring-2 focus:ring-indigo-500 ${primaryAction.className || ''}`}
        >
          {primaryAction.icon
            ? <primaryAction.icon className="h-4 w-4" aria-hidden="true" />
            : primaryAction.label}
        </Button>
      )}
      <Button
        ref={triggerRef}
        onClick={() => {
          if (!isOpen && dropdownRef.current) {
            const rect = dropdownRef.current.getBoundingClientRect();
            const offset = 8;
            const menuWidth = menuRef.current?.offsetWidth || 192;
            setMenuPosition({
              top: rect.bottom + offset,
              left: Math.max(offset, rect.right - menuWidth),
            });
          }
          setIsOpen(!isOpen);
        }}
        variant="outline"
        type="button"
        size="xs"
        title={triggerLabel}
        aria-label={triggerLabel}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        className={primaryAction
          ? 'rounded-l-none border-l-0 focus:ring-2 focus:ring-indigo-500'
          : mobileIconOnly ? 'h-8 w-8 px-0 md:h-auto md:w-auto md:px-2' : ''}
      >
        {primaryAction ? (
          <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
        ) : (
          <>
            {mobileIconOnly && <Menu className="h-4 w-4 md:hidden" aria-hidden="true" />}
            <span className={mobileIconOnly ? 'sr-only md:not-sr-only' : ''}>{triggerLabel}</span>
          </>
        )}
      </Button>

      {isOpen && (
        <div
          ref={menuRef}
          role="menu"
          aria-label={triggerLabel}
          className="fixed w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50"
          style={{ top: `${menuPosition.top}px`, left: `${menuPosition.left}px` }}
        >
          {visibleActions.map((action, index) => (
            <button
              key={index}
              role="menuitem"
              onClick={() => {
                action.onClick(item);
                setIsOpen(false);
              }}
              className={`cursor-pointer flex items-center w-full px-3 py-2 text-sm text-left hover:bg-gray-50 transition-colors ${action.className || 'text-gray-700'}`}
              type="button"
            >
              {action.icon && <action.icon size={16} className="mr-2" />}
              {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default DropdownActions;
